// The receiver journey calls of the admin SPA, on the `HttpClient` with the browser session
// cookie, each answer decoded with its closed admin contract. A non-2xx answer is kept as its
// status, the Problem's field when Power names one, the create conflict it is (a create still
// running under the same key, or a holder already connected), a 503's code, and the error code and trace id to quote
// to support, for the screen to explain. A holder's CPF or CNPJ travels only in a POST body, never
// in a URL.
import { Duration, Effect, Option, Schema } from 'effect';
import { FetchHttpClient, HttpClient, HttpClientRequest } from 'effect/http';
import { PowerRefusal, powerRefusalOf } from '@finnest/power-admin-contracts/failure';
import {
  OPIN_RECEIVER_PATH,
  OpinReceiverAccepted,
  OpinReceiverCapabilities,
  OpinReceiverConnectionCreated,
  OpinReceiverConnectionDetail,
  OpinReceiverConnectionPage,
  OpinReceiverCreateConflict,
  OpinReceiverPortfolio,
  OpinReceiverReconciliationCode,
  OpinReceiverTransmitterPage,
  OpinReceiverUnavailableCode,
} from '@finnest/power-admin-contracts/opin-receiver';
import {
  OpinReceiverClaimPage,
  OpinReceiverPolicyholderPage,
  OpinReceiverPolicyPage,
  OpinReceiverPremiumPage,
} from '@finnest/power-admin-contracts/opin-receiver/received-data';
import type {
  OpinReceiverConnectionCreate,
  OpinReceiverConnectionStatus,
  OpinReceiverPortfolioLookup,
  OpinReceiverRevocation,
  OpinReceiverRevocationReason,
} from '@finnest/power-admin-contracts/opin-receiver';
import type {
  OpinReceiverClaim,
  OpinReceiverPolicy,
  OpinReceiverPolicyholder,
  OpinReceiverPremium,
  OpinReceiverProduct,
} from '@finnest/power-admin-contracts/opin-receiver/received-data';
import type { OpinConnectionId } from '@finnest/power-admin-contracts';
import { OpinReceiverRevocationRetryReport } from '@finnest/power-admin-contracts/opin-receiver/revocation-retry';
import type { OpinReceiverRevocationRetry } from '@finnest/power-admin-contracts/opin-receiver/revocation-retry';

const UnreachableReason = Schema.Literals(['transport', 'timeout']);

/** No answer: the connection failed, the body stopped in transit, or the call ran out of time. */
export class ReceiverCallUnreachable extends Schema.TaggedError<ReceiverCallUnreachable>()(
  'ReceiverCallUnreachable',
  { reason: UnreachableReason },
) {}

/** Why a pending DCR registration attempt blocks a create, and the detail naming its command. */
export const ReceiverPendingRegistration = Schema.Struct({
  code: OpinReceiverReconciliationCode,
  detail: Schema.String,
});
export type ReceiverPendingRegistration = typeof ReceiverPendingRegistration.Type;

/**
 * Power answered a status other than 2xx; `field` names the refused field, or `request`,
 * `createConflict` the create conflict a 409 names: a create under the same idempotency key still
 * running (a retry resumes it) or the holder already connected to the transmitter,
 * `unavailable` is the code of a journey 503, `pendingRegistration` the pending DCR registration
 * attempt a create 409 names, and `refusal` the code and trace id the answer names, if it names a
 * code.
 */
export class ReceiverCallRefused extends Schema.TaggedError<ReceiverCallRefused>()(
  'ReceiverCallRefused',
  {
    status: Schema.Int,
    field: Schema.String,
    createConflict: Schema.NullOr(OpinReceiverCreateConflict),
    unavailable: Schema.NullOr(OpinReceiverUnavailableCode),
    pendingRegistration: Schema.NullOr(ReceiverPendingRegistration),
    refusal: Schema.NullOr(PowerRefusal),
  },
) {}

/** Power answered 2xx with a body outside the admin contract. */
export class ReceiverReplyInvalid extends Schema.TaggedError<ReceiverReplyInvalid>()(
  'ReceiverReplyInvalid',
  { cause: Schema.instanceOf(Schema.SchemaError) },
) {}

export type ReceiverCallFailure =
  | ReceiverCallUnreachable
  | ReceiverCallRefused
  | ReceiverReplyInvalid;

/** One receiver call of this deployment's Power API. */
export type ReceiverCall<A> = Effect.Effect<A, ReceiverCallFailure, HttpClient.HttpClient>;

/** One receiver operation, against the Power API at the base URL it is run with. */
export type ReceiverOperation<A> = (apiBaseUrl: string) => ReceiverCall<A>;

const CALL_BUDGET_SECONDS = 30;
const CALL_BUDGET = Duration.seconds(CALL_BUDGET_SECONDS);
// A retry batch sends up to 50 revocations to the receiver, five at a time: it gets longer.
const RETRY_BUDGET_SECONDS = 90;
const RETRY_BUDGET = Duration.seconds(RETRY_BUDGET_SECONDS);
const HTTP_SUCCESS_FLOOR = 200;
const HTTP_SUCCESS_CEILING = 299;
const CLOSED = { onExcessProperty: 'error' } as const;
const WHOLE_REQUEST = 'request';

const ProblemFields = Schema.fromJsonString(
  Schema.Struct({
    detail: Schema.optionalKey(Schema.String),
    errors: Schema.optionalKey(
      Schema.Array(
        Schema.Struct({
          field: Schema.optionalKey(Schema.String),
          code: Schema.optionalKey(Schema.String),
          message: Schema.optionalKey(Schema.String),
        }),
      ),
    ),
  }),
);
const decodeProblem = Schema.decodeUnknownOption(ProblemFields);
const decodeUnavailable = Schema.decodeUnknownOption(OpinReceiverUnavailableCode);
const decodePending = Schema.decodeUnknownOption(ReceiverPendingRegistration);
const decodeCreateConflict = Schema.decodeUnknownOption(OpinReceiverCreateConflict);
const HTTP_CONFLICT = 409;
const HTTP_UNAVAILABLE = 503;

const refusedFrom = (status: number, text: string): ReceiverCallRefused => {
  const problem = decodeProblem(text);
  const first = Option.flatMap(problem, ({ errors }) => Option.fromUndefinedOr(errors?.[0]));
  return ReceiverCallRefused.make({
    status,
    field: Option.getOrElse(
      Option.flatMap(first, ({ field }) => Option.fromUndefinedOr(field)),
      () => WHOLE_REQUEST,
    ),
    createConflict: Option.getOrNull(
      Option.flatMap(
        Option.filter(problem, () => status === HTTP_CONFLICT),
        ({ detail }) => decodeCreateConflict(detail),
      ),
    ),
    unavailable: Option.getOrNull(
      Option.flatMap(
        Option.filter(first, () => status === HTTP_UNAVAILABLE),
        ({ code }) => decodeUnavailable(code),
      ),
    ),
    pendingRegistration: Option.getOrNull(
      Option.flatMap(
        Option.filter(first, () => status === HTTP_CONFLICT),
        ({ code, message }) => decodePending({ code, detail: message }),
      ),
    ),
    refusal: Option.getOrNull(powerRefusalOf(text)),
  });
};

const unreachable = (reason: typeof UnreachableReason.Type): ReceiverCallUnreachable =>
  ReceiverCallUnreachable.make({ reason });

// A receiver call that gives up after `budget`.
const receiverCallWithin =
  (budget: Duration.Duration) =>
  <A>(
    request: HttpClientRequest.HttpClientRequest,
    contract: Schema.Codec<A, unknown>,
  ): ReceiverCall<A> =>
    Effect.gen(function* () {
      const client = yield* HttpClient.HttpClient;
      const response = yield* client
        .execute(HttpClientRequest.acceptJson(request))
        .pipe(Effect.mapError(() => unreachable('transport')));
      const text = yield* response.text.pipe(Effect.mapError(() => unreachable('transport')));
      if (response.status < HTTP_SUCCESS_FLOOR || response.status > HTTP_SUCCESS_CEILING) {
        return yield* refusedFrom(response.status, text);
      }
      return yield* Schema.decodeEffect(Schema.fromJsonString(contract))(text, CLOSED).pipe(
        Effect.mapError((cause) => ReceiverReplyInvalid.make({ cause })),
      );
    }).pipe(
      Effect.timeoutOrElse({
        duration: budget,
        orElse: () => Effect.fail(unreachable('timeout')),
      }),
      Effect.provideService(FetchHttpClient.RequestInit, { credentials: 'include' }),
      Effect.provideService(HttpClient.TracerPropagationEnabled, false),
    );

const receiverCall = receiverCallWithin(CALL_BUDGET);

const urlOf = (apiBaseUrl: string, path: string): string =>
  `${apiBaseUrl}${OPIN_RECEIVER_PATH}${path}`;

// A POST body of one of the admin contracts, whose decoded and wire shapes are the same.
const postedWithin =
  (budget: Duration.Duration) =>
  <A>(
    request: HttpClientRequest.HttpClientRequest,
    body:
      | OpinReceiverConnectionCreate
      | OpinReceiverPortfolioLookup
      | OpinReceiverRevocation
      | OpinReceiverRevocationRetry,
    answer: Schema.Codec<A, unknown>,
  ): ReceiverCall<A> =>
    receiverCallWithin(budget)(HttpClientRequest.bodyJsonUnsafe(request, body), answer);

const posted = postedWithin(CALL_BUDGET);

const pageParam =
  (page: number) =>
  (request: HttpClientRequest.HttpClientRequest): HttpClientRequest.HttpClientRequest =>
    HttpClientRequest.setUrlParam(request, 'page', String(page));

const connectionPath = (id: string): string => `/connections/${encodeURIComponent(id)}`;

export type TransmitterSearch = { readonly q: string; readonly page: number };

export const searchTransmitters =
  (search: TransmitterSearch): ReceiverOperation<OpinReceiverTransmitterPage> =>
  (apiBaseUrl) =>
    receiverCall(
      HttpClientRequest.get(urlOf(apiBaseUrl, '/transmitters')).pipe(
        HttpClientRequest.setUrlParam('q', search.q),
        pageParam(search.page),
      ),
      OpinReceiverTransmitterPage,
    );

/**
 * The connection list's filters: an application's client id (empty for every one), the statuses
 * asked for (none for every one) and the search term as typed (blank for no search).
 */
export type ConnectionFilters = {
  readonly clientId: string;
  readonly statuses: readonly OpinReceiverConnectionStatus[];
  readonly search: string;
  readonly page: number;
};

// Power refuses a term with surrounding spaces, so the term goes trimmed, and only when one is left.
const filtered =
  ({ clientId, statuses, search }: ConnectionFilters) =>
  (request: HttpClientRequest.HttpClientRequest): HttpClientRequest.HttpClientRequest => {
    const term = search.trim();
    return HttpClientRequest.appendUrlParams(request, [
      ...(clientId === '' ? [] : [['clientId', clientId] as const]),
      ...statuses.map((status) => ['status', status] as const),
      ...(term === '' ? [] : [['q', term] as const]),
    ]);
  };

export const listConnections =
  (filters: ConnectionFilters): ReceiverOperation<OpinReceiverConnectionPage> =>
  (apiBaseUrl) =>
    receiverCall(
      HttpClientRequest.get(urlOf(apiBaseUrl, '/connections')).pipe(
        filtered(filters),
        pageParam(filters.page),
      ),
      OpinReceiverConnectionPage,
    );

/** One connection, the records its syncs stored and the resources its consent shares. */
export const readConnection =
  (id: string): ReceiverOperation<OpinReceiverConnectionDetail> =>
  (apiBaseUrl) =>
    receiverCall(
      HttpClientRequest.get(urlOf(apiBaseUrl, connectionPath(id))),
      OpinReceiverConnectionDetail,
    );

/** A create: the form's body and the key made when the form opened. */
export type ConnectionCreate = {
  readonly body: OpinReceiverConnectionCreate;
  readonly idempotencyKey: string;
};

export const createConnection =
  ({ body, idempotencyKey }: ConnectionCreate): ReceiverOperation<OpinReceiverConnectionCreated> =>
  (apiBaseUrl) =>
    posted(
      HttpClientRequest.post(urlOf(apiBaseUrl, '/connections')).pipe(
        HttpClientRequest.setHeader('x-idempotency-key', idempotencyKey),
      ),
      body,
      OpinReceiverConnectionCreated,
    );

export const syncConnection =
  (id: string): ReceiverOperation<typeof OpinReceiverAccepted.Type> =>
  (apiBaseUrl) =>
    receiverCall(
      HttpClientRequest.post(urlOf(apiBaseUrl, `${connectionPath(id)}/sync`)),
      OpinReceiverAccepted,
    );

/** A revocation: the connection and the reason the operator chose. */
export type ConnectionRevocation = {
  readonly id: string;
  readonly reason: OpinReceiverRevocationReason;
};

export const revokeConnection =
  ({ id, reason }: ConnectionRevocation): ReceiverOperation<typeof OpinReceiverAccepted.Type> =>
  (apiBaseUrl) =>
    posted(
      HttpClientRequest.post(urlOf(apiBaseUrl, `${connectionPath(id)}/revoke`)),
      { reason },
      OpinReceiverAccepted,
    );

/**
 * Sends the pending revocations of `connectionIds` again, each with the reason it was first asked
 * with; the report holds one outcome per connection.
 */
export const retryRevocations =
  (
    connectionIds: readonly OpinConnectionId[],
  ): ReceiverOperation<OpinReceiverRevocationRetryReport> =>
  (apiBaseUrl) =>
    postedWithin(RETRY_BUDGET)(
      HttpClientRequest.post(urlOf(apiBaseUrl, '/revocations/retry')),
      { connectionIds },
      OpinReceiverRevocationRetryReport,
    );

/** The revocation reasons the session's operator may choose. */
export const readCapabilities: ReceiverOperation<OpinReceiverCapabilities> = (apiBaseUrl) =>
  receiverCall(HttpClientRequest.get(urlOf(apiBaseUrl, '/capabilities')), OpinReceiverCapabilities);

export const holderPortfolio =
  (lookup: OpinReceiverPortfolioLookup): ReceiverOperation<OpinReceiverPortfolio> =>
  (apiBaseUrl) =>
    posted(
      HttpClientRequest.post(urlOf(apiBaseUrl, '/holders/portfolio')),
      lookup,
      OpinReceiverPortfolio,
    );

/** One received record, tagged with its product. */
export type ReceivedRecord =
  | { readonly product: 'policies'; readonly record: OpinReceiverPolicy }
  | { readonly product: 'premiums'; readonly record: OpinReceiverPremium }
  | { readonly product: 'claims'; readonly record: OpinReceiverClaim }
  | { readonly product: 'policyholders'; readonly record: OpinReceiverPolicyholder };

/** One page of a connection's received records for a product, and its totals. */
export type ReceivedPage = {
  readonly records: readonly ReceivedRecord[];
  readonly meta: {
    readonly page: number;
    readonly totalRecords: number;
    readonly totalPages: number;
  };
};

const RECEIVED: {
  readonly [P in OpinReceiverProduct]: (
    request: HttpClientRequest.HttpClientRequest,
  ) => ReceiverCall<ReceivedPage>;
} = {
  policies: (request) =>
    Effect.map(receiverCall(request, OpinReceiverPolicyPage), ({ data, meta }) => ({
      records: data.map((record) => ({ product: 'policies', record }) as const),
      meta,
    })),
  premiums: (request) =>
    Effect.map(receiverCall(request, OpinReceiverPremiumPage), ({ data, meta }) => ({
      records: data.map((record) => ({ product: 'premiums', record }) as const),
      meta,
    })),
  claims: (request) =>
    Effect.map(receiverCall(request, OpinReceiverClaimPage), ({ data, meta }) => ({
      records: data.map((record) => ({ product: 'claims', record }) as const),
      meta,
    })),
  policyholders: (request) =>
    Effect.map(receiverCall(request, OpinReceiverPolicyholderPage), ({ data, meta }) => ({
      records: data.map((record) => ({ product: 'policyholders', record }) as const),
      meta,
    })),
};

/** Which page of which product of which connection. */
export type ReceivedSelection = {
  readonly id: string;
  readonly product: OpinReceiverProduct;
  readonly page: number;
};

/** One page of a connection's received records for a product. */
export const receivedData =
  ({ id, product, page }: ReceivedSelection): ReceiverOperation<ReceivedPage> =>
  (apiBaseUrl) =>
    RECEIVED[product](
      HttpClientRequest.get(urlOf(apiBaseUrl, `${connectionPath(id)}/${product}`)).pipe(
        pageParam(page),
      ),
    );
